import {
  filterFixedProducts,
  filterUsageProducts,
  isPromotionProduct,
  unsetProductDatesProcessor,
} from '@vy/services';
import type { NewPlan } from '@vy/types';
import { CONTRACT_TYPES } from '@vy/types';
import { isEmptyString, isNotEmptyString, isNotNullOrUndefined, isNullOrUndefined, newId } from '@vy/utils';
import React, { useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { FormProvider, useForm } from 'react-hook-form';
import useFormPersist from 'react-hook-form-persist';
import { useSearchParams } from 'react-router-dom';
import { ErrorLabel } from '../../../../../components/design/ErrorLabel';
import { Spinner } from '../../../../../components/design/Spinner';
import { LocalStorageKeys } from '../../../../../constants/local-storage-keys';
import { useCreatePlanContext } from '../../../../../contexts/create-plan-context';
import { PLAN_CREATION_REASONS } from '../../../../../contexts/create-plan-context/create-plan-context-types/plan-create-reason';
import { PLAN_CREATION_STEPS } from '../../../../../contexts/create-plan-context/create-plan-context-types/plan-creation-step';
import { useExtractPlan } from '../../../../../hooks/requests/use-extract-plan';
import { usePlanById } from '../../../../../hooks/requests/use-plan-by-id';
import { useVyNavigate } from '../../../../../hooks/use-vy-navigate';
import { getProRataSetting } from '../../../../../services/get-pro-ration-setting-value';
import { processRateMultiplierPlanProducts } from '../../../../../services/rate-multiplier-product-processor';
import { runMutation } from '../../../../../utils/vy-mutation';
import type { AssignCustomerForm } from '../../../GetStartedPlansPage/types/assign-customer-dialog-form-types/assign-customer-form-schema';
import { createDiscountStrippingResolver } from '../../../../../utils/strip-incomplete-discounts';
import { getCreatePlanDefaultValues } from '../../create-plan-page-consts/create-plan-default-values';
import { DROP_ZONE_CONFIG } from '../../create-plan-page-consts/dropzone-config-consts';
import { useGetContractPrerequisites } from '../../create-plan-page-hooks/useGetContractPrerequisites';
import { useClearUpliftOnShortDuration } from '../../create-plan-page-hooks/useClearUpliftOnShortDuration';
import type { NewPlanForm } from './create-plan-form-types/new-plan-form';
import type { PlanInputForm } from './create-plan-form-types/plan-input-form';
import { PlanInputFormSchema } from './create-plan-form-types/plan-input-form';
import { getPlanSettingsInputFormSchema } from './create-plan-form-types/plan-settings-input-form';

type CreatePlanFormProps = {
  startDate?: Date;
  customerId?: string;
  children?: React.ReactNode;
};

export const CreatePlanForm: React.FC<CreatePlanFormProps> = ({ children }) => {
  const navigate = useVyNavigate();
  const extractPlanMutation = useExtractPlan();
  const [searchParams] = useSearchParams();
  const cloneFromPlanId = searchParams.get('cloneFromPlanId') ?? undefined;
  const {
    planCreationStepper: { currentStep },
    planCreationReason,
  } = useCreatePlanContext();
  const contractPrerequisites = useGetContractPrerequisites();

  const isParentAgreement = contractPrerequisites?.contractType === CONTRACT_TYPES.ParentAgreement;

  const { data: templatePlanResponse, isLoading: isLoadingPlan } = usePlanById(cloneFromPlanId ?? '', {
    enabled: isNotEmptyString(cloneFromPlanId),
    suspense: false,
    refetchOnWindowFocus: false,
  });

  const entityFormSchema = getPlanSettingsInputFormSchema(
    planCreationReason === PLAN_CREATION_REASONS.NewPlan,
    isParentAgreement,
  );
  const activeFormSchema =
    currentStep === PLAN_CREATION_STEPS.settings ? entityFormSchema : PlanInputFormSchema;

  const defaultValues = getCreatePlanDefaultValues({
    startDate: contractPrerequisites?.startDate,
    customerId: contractPrerequisites?.customerId,
    contractType: contractPrerequisites?.contractType,
  });

  if (isNotNullOrUndefined(contractPrerequisites) && hasPrerequisitesChanged(contractPrerequisites)) {
    localStorage.removeItem(LocalStorageKeys.CreatePlan);
  }

  const methods = useForm<PlanInputForm, unknown, NewPlanForm>({
    resolver: createDiscountStrippingResolver(activeFormSchema),
    defaultValues,
  });

  useFormPersist(LocalStorageKeys.CreatePlan, {
    watch: methods.watch,
    setValue: methods.setValue,
    storage: localStorage,
  });

  useClearUpliftOnShortDuration(methods);

  useEffect(() => {
    return () => {
      if (isNotEmptyString(cloneFromPlanId)) {
        localStorage.removeItem(LocalStorageKeys.CreatePlan);
      }
    };
  }, []);

  const { getRootProps } = useDropzone({
    ...DROP_ZONE_CONFIG,
    noClick: true,
    noKeyboard: true,
    onDrop: async (acceptedFiles) => {
      await runMutation(
        extractPlanMutation,
        { file: acceptedFiles[0] },
        {
          pending: 'Extracting plan...',
          success: 'Plan extracted',
          error: 'Failed to extract plan',
        },
        {
          onSuccess: (newPlan: NewPlan) => {
            const { products, ...planWithoutProducts } = newPlan;
            const existingProducts = products.map((product) => ({
              ...product,
              id: newId(),
              variants: [
                {
                  ...product.variants[0],
                  id: newId(),
                },
              ],
            }));

            const fixedProductsWithPromotions = filterFixedProducts(
              existingProducts,
            ) as PlanInputForm['fixedProducts'];
            const fixedProducts = fixedProductsWithPromotions?.filter(
              (product) => !isPromotionProduct(product),
            );
            const promotionProducts = fixedProductsWithPromotions?.filter((product) =>
              isPromotionProduct(product),
            );
            const usageProducts = filterUsageProducts(existingProducts) as PlanInputForm['usageProducts'];

            methods.reset(
              {
                name: planWithoutProducts.name,
                basedFromPlanId: undefined,
                billingData: planWithoutProducts.billingData ?? undefined,
                status: planWithoutProducts.status,
                trial: planWithoutProducts.trial,
                promotionProducts,
                fixedProducts,
                usageProducts,
              },
              { keepDefaultValues: true },
            );
          },
        },
      );
    },
  });

  useEffect(() => {
    return () => {
      if (isParentAgreement) {
        localStorage.removeItem(LocalStorageKeys.CreatePlan);
      }
    };
  }, [isParentAgreement]);

  useEffect(() => {
    const storedCloneFromPlanId = methods.watch('basedFromPlanId');

    if (isEmptyString(cloneFromPlanId) && isNotNullOrUndefined(storedCloneFromPlanId)) {
      navigate(`/plans/create?cloneFromPlanId=${storedCloneFromPlanId}`);
    }
  }, [methods, navigate, cloneFromPlanId]);

  useEffect(() => {
    if (isNotEmptyString(cloneFromPlanId) && isNotNullOrUndefined(templatePlanResponse)) {
      const { products, ...planWithoutProducts } = templatePlanResponse.plan;
      const rateMultiplierAdjustedProducts = processRateMultiplierPlanProducts(products);
      const productsWithoutDates = unsetProductDatesProcessor(rateMultiplierAdjustedProducts);
      const fixedProducts = filterFixedProducts(productsWithoutDates) as PlanInputForm['fixedProducts'];
      const promotionProducts = fixedProducts?.filter(isPromotionProduct);
      const fixedNonPromotionProducts = fixedProducts?.filter((product) => !isPromotionProduct(product));

      const usageProducts = filterUsageProducts(productsWithoutDates) as PlanInputForm['usageProducts'];

      methods.reset({
        ...planWithoutProducts,
        name: `Copy of ${templatePlanResponse.plan.name}`,
        basedFromPlanId: cloneFromPlanId,
        proRataSetting: getProRataSetting(templatePlanResponse.plan),
        fixedProducts: fixedNonPromotionProducts,
        promotionProducts,
        usageProducts,
        customerId: contractPrerequisites?.customerId,
        startDate: contractPrerequisites?.startDate,
      });
    }
  }, [methods, cloneFromPlanId, templatePlanResponse]);

  if (isLoadingPlan) {
    return <Spinner />;
  }

  return (
    <FormProvider {...methods}>
      <form className="flex h-full flex-col" {...getRootProps()}>
        {children}
      </form>

      {isNotNullOrUndefined(methods.formState.errors.root) && (
        <ErrorLabel errorMessage={methods.formState.errors.root?.message ?? ''} />
      )}
    </FormProvider>
  );
};

function hasPrerequisitesChanged(prerequisites: AssignCustomerForm): boolean {
  const storedPrerequisites = localStorage.getItem(LocalStorageKeys.CreatePlan);

  if (isNullOrUndefined(storedPrerequisites)) {
    return false;
  }

  try {
    const parsed = JSON.parse(storedPrerequisites);

    return (
      parsed.customerId !== prerequisites.customerId ||
      String(parsed.startDate) !== String(prerequisites.startDate) ||
      parsed.contractType !== prerequisites.contractType
    );
  } catch {
    return true;
  }
}
